import { ReactElement, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { DetailsPanel, FormatInformation, Table, Typography } from '@weareopenr/component-library';
import { DetailedInventoryItem } from 'src/api/getInventoryItem/types';
import { Routes } from 'src/routes';
import styles from './InventoryOverviewTab.module.scss';

interface InventoryOverviewTabProps {
  inventoryItem: DetailedInventoryItem;
}

type TIngredientRow = {
  recipeIngredientId: string;
  ingredientProductId: string;
  ingredientName: string;
  shortName: string;
  numberOfDispenses: number;
  preparationOrder: number;
  productSize: string;
  substitutions: string;
};

type TExternalIdentifierRow = {
  externalSystemId: string;
  externalId: string;
  name: string;
  externalSystemName?: string | null;
};

const ingredientColumnHelper = createColumnHelper<TIngredientRow>();

const ingredientColumns = [
  ingredientColumnHelper.accessor('ingredientName', { header: 'Ingredient' }),
  ingredientColumnHelper.accessor('shortName', { header: 'Short Name', size: 150 }),
  ingredientColumnHelper.accessor('numberOfDispenses', { header: 'Dispenses', size: 120 }),
  ingredientColumnHelper.accessor('preparationOrder', { header: 'Prep Order', size: 120 }),
  ingredientColumnHelper.accessor('productSize', { header: 'Product Size', size: 140 }),
  ingredientColumnHelper.accessor('substitutions', { header: 'Substitutions' }),
];

const externalIdentifierColumnHelper = createColumnHelper<TExternalIdentifierRow>();

const externalIdentifierColumns = [
  externalIdentifierColumnHelper.accessor('externalSystemName', { header: 'System' }),
  externalIdentifierColumnHelper.accessor('externalId', { header: 'External ID' }),
];

const getCalories = (inventoryItem: DetailedInventoryItem) => {
  return inventoryItem.nutritional_information
    .filter((nutrition) => nutrition.name === 'energy_kcal' || nutrition.name === 'energy')
    .map((nutrition) => {
      const valueLow = Math.round(nutrition.value_low);
      const valueHigh = Math.round(nutrition.value_high);
      return valueHigh === valueLow ? valueHigh : `${valueLow} - ${valueHigh}`;
    })[0]
    ?.toString();
};

const IngredientsEmptyState = () => (
  <div className={styles['table-empty-state']}>
    <Typography colour="primary">No ingredients found</Typography>
  </div>
);

const InventoryOverviewTab = ({ inventoryItem }: InventoryOverviewTabProps): ReactElement => {
  const navigate = useNavigate();
  const isRecipe = inventoryItem.type === 'RECIPE';

  const navigateToInventoryItem = useCallback(
    (recipeId: string): void => navigate(`${Routes.INVENTORY}/${recipeId}`, { state: { from: Routes.INVENTORY } }),
    [navigate],
  );

  const isIngredientRowClickable = useCallback(
    (row: TIngredientRow) => () => navigateToInventoryItem(row.ingredientProductId),
    [navigateToInventoryItem],
  );

  const ingredientRows = useMemo<TIngredientRow[]>(
    () =>
      inventoryItem.ingredients?.map((ingredient) => ({
        recipeIngredientId: ingredient.recipe_ingredient_id,
        ingredientProductId: ingredient.ingredient_product_id,
        ingredientName: ingredient.ingredient_name,
        shortName: ingredient.ingredient_short_name || '-',
        numberOfDispenses: ingredient.number_of_dispenses,
        preparationOrder: ingredient.preparation_order,
        productSize: ingredient.product_size || '-',
        substitutions: ingredient.substitutions.map((sub) => sub.product_name).join(', ') || '-',
      })) || [],
    [inventoryItem],
  );

  const externalIdentifierRows = useMemo<TExternalIdentifierRow[]>(
    () =>
      inventoryItem.external_identifiers?.map((identifier) => ({
        externalSystemId: identifier.external_system_id,
        externalId: identifier.external_id,
        name: identifier.name || '-',
        externalSystemName: identifier.external_system_name,
      })) || [],
    [inventoryItem],
  );

  return (
    <div className={styles['overview-container']}>
      <DetailsPanel title={isRecipe ? 'Recipe Details' : 'Goods Details'}>
        <DetailsPanel.Row>
          <DetailsPanel.Column>
            <FormatInformation heading="Openr ID">{inventoryItem.recipe_id}</FormatInformation>
          </DetailsPanel.Column>
          <DetailsPanel.Column>
            <FormatInformation heading="Code">{inventoryItem.code || '-'}</FormatInformation>
          </DetailsPanel.Column>
        </DetailsPanel.Row>
        <DetailsPanel.Row>
          <DetailsPanel.Column>
            <FormatInformation heading="Name">{inventoryItem.name || '-'}</FormatInformation>
          </DetailsPanel.Column>
          <DetailsPanel.Column>
            <FormatInformation heading="Short Name">{inventoryItem.short_name || '-'}</FormatInformation>
          </DetailsPanel.Column>
        </DetailsPanel.Row>
        <DetailsPanel.Row>
          <DetailsPanel.Column>
            <FormatInformation heading="Description">{inventoryItem.description || '-'}</FormatInformation>
            <FormatInformation heading="Calories">
              {getCalories(inventoryItem) || 'No Calorie Information Provided'}
            </FormatInformation>
          </DetailsPanel.Column>
        </DetailsPanel.Row>
      </DetailsPanel>
      {isRecipe && (
        <DetailsPanel title="Ingredients">
          <DetailsPanel.Row>
            <DetailsPanel.Column>
              <FormatInformation heading="Ingredient Declaration">
                {inventoryItem.ingredient_declaration || '-'}
              </FormatInformation>

              <FormatInformation heading="Ingredients List">
                <div className={styles['table-container']}>
                  <Table
                    data={ingredientRows}
                    columns={ingredientColumns}
                    isRowClickable={isIngredientRowClickable}
                    EmptyState={IngredientsEmptyState}
                  />
                </div>
              </FormatInformation>
            </DetailsPanel.Column>
          </DetailsPanel.Row>
        </DetailsPanel>
      )}
      {externalIdentifierRows.length > 0 && (
        <DetailsPanel title="External Identifiers">
          <div className={styles['table-container']}>
            <Table data={externalIdentifierRows} columns={externalIdentifierColumns} />
          </div>
        </DetailsPanel>
      )}
    </div>
  );
};

export default InventoryOverviewTab;
